import { MantineThemeOverride } from '@mantine/core'
import { routexAmber, routexGold, routexGray, RoutexTokens } from './palette'

const T = RoutexTokens.light

/**
 * Tema claro do RouteX Admin.
 *
 * Mesma estrutura de tema do `vendax-promoter-admin` e do `doclenz-admin` — paleta central em
 * `palette.ts`, overrides de componente aqui, tokens CSS em `styles/index.css` — com a paleta
 * âmbar do RouteX.
 *
 * A barra lateral e os destaques do `ArchbaseAdminMainLayout` derivam da cor primária do tema,
 * então trocar a paleta aqui reveste o layout inteiro.
 */
export const AppThemeLight: MantineThemeOverride = {
  primaryColor: 'appPrimary',
  primaryShade: 6,
  colors: {
    appPrimary: routexAmber,
    appAccent: routexGold,
    appGray: routexGray,
    routexAmber,
    routexGold,
  },
  // `lg` = 16px é o raio padrão do produto.
  defaultRadius: 'lg',
  radius: {
    xs: '6px',
    sm: '9px',
    md: '11px',
    lg: '16px',
    xl: '22px',
  },
  fontFamily: 'Inter, system-ui, sans-serif',
  headings: {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontWeight: '700',
  },
  shadows: {
    xs: '0 4px 14px rgba(74,31,7,.04)',
    sm: '0 6px 20px rgba(74,31,7,.06)',
    md: T.shadow,
    lg: '0 14px 40px rgba(74,31,7,.10)',
    xl: '0 20px 56px rgba(74,31,7,.12)',
  },
  components: {
    AppShell: {
      styles: {
        navbar: {
          border: 'none',
          backgroundImage: RoutexTokens.sidebarGradient,
          backgroundColor: RoutexTokens.espresso950,
        },
        header: {
          borderBottom: `1px solid ${T.border}`,
          backgroundColor: T.surface,
        },
        // O AppShell pinta a área de conteúdo com um cinza próprio, que cobriria o fundo
        // definido no body. Os halos precisam vir daqui; `fixed` impede que deslizem no scroll.
        main: {
          backgroundColor: T.bg,
          backgroundImage:
            'radial-gradient(circle at 85% 8%, rgba(245,158,11,.10), transparent 26%),' +
            'radial-gradient(circle at 15% 88%, rgba(180,83,9,.06), transparent 28%)',
          backgroundAttachment: 'fixed',
        },
      },
    },
    Card: {
      defaultProps: {
        shadow: 'md',
        radius: 'lg',
        withBorder: true,
      },
      styles: {
        root: {
          backgroundColor: T.surface,
          borderColor: T.border,
          color: T.text,
        },
      },
    },
    Paper: {
      styles: {
        root: {
          backgroundColor: T.surface,
          borderColor: T.border,
          color: T.text,
        },
      },
    },
    Button: {
      defaultProps: {
        radius: 'sm',
      },
    },
    Badge: {
      styles: {
        root: {
          textTransform: 'none',
        },
      },
    },
    Table: {
      styles: {
        th: {
          color: T.muted,
          fontWeight: 700,
          backgroundColor: T.surface2,
        },
      },
    },
    Code: {
      styles: {
        root: {
          backgroundColor: T.surface3,
          color: T.text,
        },
      },
    },
    Tooltip: {
      styles: {
        tooltip: {
          fontWeight: '500',
        },
      },
    },
  },
}

/** Cores do aplicativo, para referência rápida em componentes. */
export const AppColors = {
  primary: RoutexTokens.amber600,
  accent: RoutexTokens.gold,
  backgroundDark: RoutexTokens.dark.bg,
  surface: RoutexTokens.dark.surface,
  surfaceDark: RoutexTokens.dark.surface2,
  backgroundLight: T.surface,
  success: '#12915a',
  error: '#ef5b4c',
  warning: RoutexTokens.gold,
  info: RoutexTokens.amber500,
  textPrimary: T.text,
  textSecondary: T.muted,
  gradient: RoutexTokens.primaryGradient,
} as const

/** Cores por situação de job de roteirização. */
export const StatusColors = {
  ativo: AppColors.success,
  inativo: AppColors.textSecondary,
  processando: RoutexTokens.amber500,
  concluido: AppColors.success,
  falhou: AppColors.error,
  cancelado: AppColors.textSecondary,
} as const
